import axios from 'axios';
import { auth } from '../firebase';

const API_BASE_URL = process.env.REACT_APP_API_URL;
const AGENTS_API_URL = process.env.REACT_APP_AGENTS_API_URL;

export const teamsApi = {
  fetchTeams: async (portalId) => {
    if (!portalId) throw new Error('Portal ID is required');
    try {
      const response = await axios.get(`${API_BASE_URL}/teams`, {
        params: { limit: 1000, portal_id: portalId },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching teams:', error);
      throw error;
    }
  },

  fetchTeamById: async (teamId) => {
    if (!teamId) throw new Error('Team ID is required');
    try {
      const response = await axios.get(`${API_BASE_URL}/teams/${teamId}`);
      return response.data; // returns { team, members } — caller should extract team
    } catch (error) {
      console.error('Error fetching team by ID:', error);
      throw error;
    }
  },

  fetchTeamRules: async (hsTeamId) => {
    if (!hsTeamId) throw new Error('Team ID is required');
    const token = await auth.currentUser?.getIdToken(true);
    const response = await axios.get(`${API_BASE_URL}/team-rules/by-team/${hsTeamId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  createTeamRule: async (ruleData) => {
    const token = await auth.currentUser?.getIdToken(true);
    const response = await axios.post(`${API_BASE_URL}/team-rules`, ruleData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  },

  deleteTeamRule: async (ruleId) => {
    const token = await auth.currentUser?.getIdToken(true);
    const response = await axios.delete(`${API_BASE_URL}/team-rules/rule/${ruleId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  addConditionToRule: async (ruleId, condition) => {
    const token = await auth.currentUser?.getIdToken(true);
    const payload = {
      hs_team_id: condition.hs_team_id,
      hs_portal_id: condition.hs_portal_id,
      object_type: condition.object_type || 'contact',
      property: condition.field_name,
      operator: condition.operator,
      property_value: condition.value || '',
      property_type: condition.property_type || 'string',
    };
  
    const response = await axios.post(
      `${API_BASE_URL}/team-rules/rule/${ruleId}/condition`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );
    return response.data.condition;
  },
  

  deleteConditionFromRule: async (ruleId, conditionId) => {
    const token = await auth.currentUser?.getIdToken(true);
    const response = await axios.delete(
      `${API_BASE_URL}/team-rules/rule/${ruleId}/condition/${conditionId}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return response.data;
  },

  createTeam: async (teamData) => {
    const token = await auth.currentUser?.getIdToken(true);
    const response = await axios.post(`${API_BASE_URL}/teams`, teamData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  },

  updateTeam: async (teamId, teamData) => {
    if (!teamId) throw new Error('Team ID is required');
    try {
      const token = await auth.currentUser?.getIdToken(true);
      if (!token) throw new Error('Authentication required');

      const response = await axios.put(`${API_BASE_URL}/teams/${teamId}`, teamData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error updating team:', error);
      throw error;
    }
  },
  exportTeamMembers: async (teamId) => {
    if (!teamId) throw new Error('Team ID is required');
    try {
      const token = await auth.currentUser?.getIdToken(true);
      const response = await axios.get(`${API_BASE_URL}/teams/${teamId}/members/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'text',
      });
      return response;
    } catch (error) {
      console.error('Error exporting team members:', error);
      throw error;
    }
  },
  importTeamMembers: async (teamId, csvText) => {
    if (!teamId) throw new Error('Team ID is required');
    if (!csvText) throw new Error('CSV content is required');
    try {
      const token = await auth.currentUser?.getIdToken(true);
      const response = await axios.post(`${API_BASE_URL}/teams/${teamId}/members/import`, csvText, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'text/csv',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error importing team members:', error);
      throw error;
    }
  },

  deleteTeam: async (teamId) => {
    if (!teamId) throw new Error('Team ID is required');
    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) throw new Error('Authentication required');

      const response = await axios.delete(`${API_BASE_URL}/teams/${teamId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error deleting team:', error);
      throw error;
    }
  }
};

export const membersApi = {
  fetchMemberById: async (memberId) => {
    if (!memberId) throw new Error('Member ID is required');
    try {
      const response = await axios.get(`${API_BASE_URL}/members/${memberId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching member by ID:', error);
      throw error;
    }
  },

  updateMember: async (memberId, memberData) => {
    if (!memberId) throw new Error('Member ID is required');

    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) throw new Error('Authentication required');

      //const requestBody = { member: memberData };
      const requestBody = { member: { id: Number(memberId), ...memberData } };

      const response = await axios.put(`${API_BASE_URL}/members/${memberId}`, requestBody, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      return response.data;
    } catch (error) {
      console.error('Error updating member:', error);
      throw error;
    }
  },

  fetchHubspotOwners: async (portalId) => {
    if (!portalId) throw new Error('Portal ID is required');
    try {
      console.log('DEBUG: Fetching owners for portal ID:', portalId);
      const token = await auth.currentUser?.getIdToken();
      const response = await axios.get(`${API_BASE_URL}/members/hubspot-owners`, {
        params: { portal_id: portalId },
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching owners:', error);
      throw error;
    }
  },

  fetchPortalMembers: async (portalId, options = {}) => {
    if (!portalId) throw new Error('Portal ID is required');
    const { limit = 1000, offset = 0 } = options;
    try {
      const token = await auth.currentUser?.getIdToken();
      const response = await axios.get(`${API_BASE_URL}/members`, {
        params: { portal_id: portalId, limit, offset },
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching portal members:', error);
      throw error;
    }
  },

  fetchOwners: async (portalId) => {
    if (!portalId) throw new Error('Portal ID is required');
    try {
      const response = await axios.get(`${API_BASE_URL}/members/hubspot-owners`, {
        params: { portal_id: portalId },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching owners:', error);
      throw error;
    }
  },

  addMemberToTeam: async (teamId, memberData) => {
    if (!teamId) throw new Error('Team ID is required');
    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) throw new Error('Authentication required');

      const response = await axios.post(`${API_BASE_URL}/members/teams/${teamId}`, memberData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error adding member to team:', error);
      throw error;
    }
  },

  removeMemberFromTeam: async (teamId, memberId) => {
    if (!teamId || !memberId) throw new Error('Team ID and Member ID are required');
    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) throw new Error('Authentication required');

      const response = await axios.delete(`${API_BASE_URL}/members/teams/${teamId}/${memberId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error removing member from team:', error);
      throw error;
    }
  }
};


export const fieldOptionsApi = {
  fetchProperties: async (objectType, portalId = null) => {
    if (!objectType) throw new Error('Object type is required');
    try {
      const token = await auth.currentUser?.getIdToken(true);
      const params = portalId ? { portal_id: portalId } : {};
      const response = await axios.get(`${API_BASE_URL}/field-options/properties/${objectType}`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching properties:', error);
      throw error;
    }
  },

  fetchOperators: async (propertyType) => {
    if (!propertyType) throw new Error('Property type is required');
    try {
      const token = await auth.currentUser?.getIdToken(true);
      const response = await axios.get(`${API_BASE_URL}/field-options/operators/${propertyType}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching operators:', error);
      throw error;
    }
  }
};

export const schedulesApi = {
  // Get agent schedule and time off
  fetchAgentSchedule: async (memberId, portalId) => {
    if (!memberId) throw new Error('Member ID is required');
    try {
      const token = await auth.currentUser?.getIdToken(true);
      const response = await axios.get(`${API_BASE_URL}/schedules/${memberId}`, {
        params: { portal_id: portalId },
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching agent schedule:', error);
      throw error;
    }
  },

  // Update agent schedule and time off
  updateAgentSchedule: async (memberId, scheduleData, portalId) => {
    if (!memberId) throw new Error('Member ID is required');
    if (!scheduleData) throw new Error('Schedule data is required');
    
    try {
      const token = await auth.currentUser?.getIdToken(true);
      const response = await axios.put(`${API_BASE_URL}/schedules/${memberId}`,  { ...scheduleData, hs_portal_id: portalId }, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error updating agent schedule:', error);
      throw error;
    }
  },

  // Get availability status for all agents in a portal
  fetchPortalAvailability: async (portalId) => {
    if (!portalId) throw new Error('Portal ID is required');
    try {
      const token = await auth.currentUser?.getIdToken(true);
      const response = await axios.get(`${API_BASE_URL}/schedules/availability/${portalId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching portal availability:', error);
      throw error;
    }
  },

  exportSchedulesCsv: async (portalId) => {
    if (!portalId) throw new Error('Portal ID is required');
    try {
      const token = await auth.currentUser?.getIdToken(true);
      const response = await axios.get(`${API_BASE_URL}/schedules-admin/export`, {
        params: { portal_id: portalId },
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error('Error exporting schedules:', error);
      throw error;
    }
  },

  importSchedulesCsv: async (portalId, csvText) => {
    if (!portalId) throw new Error('Portal ID is required');
    if (!csvText) throw new Error('CSV text is required');
    try {
      const token = await auth.currentUser?.getIdToken(true);
      const response = await axios.post(`${API_BASE_URL}/schedules-admin/import`, csvText, {
        params: { portal_id: portalId },
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'text/csv',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error importing schedules:', error);
      throw error;
    }
  },
};

export const attributesApi = {
  // List all attributes for a portal
  fetchAttributes: async (portalId) => {
    if (!portalId) throw new Error('Portal ID is required');
    try {
      const token = await auth.currentUser?.getIdToken(true);
      const response = await axios.get(`${API_BASE_URL}/attributes`, {
        params: { portal_id: portalId, limit: 100 },
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching attributes:', error);
      throw error;
    }
  },

  // Get a specific attribute by ID
  fetchAttribute: async (id) => {
    if (!id) throw new Error('Attribute ID is required');
    try {
      const token = await auth.currentUser?.getIdToken(true);
      const response = await axios.get(`${API_BASE_URL}/attributes/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching attribute:', error);
      throw error;
    }
  },

  // Create a new attribute
  createAttribute: async (attributeData) => {
    if (!attributeData) throw new Error('Attribute data is required');
    try {
      const token = await auth.currentUser?.getIdToken(true);
      const response = await axios.post(`${API_BASE_URL}/attributes`, attributeData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error creating attribute:', error);
      throw error;
    }
  },

  // Update an existing attribute
  updateAttribute: async (id, attributeData) => {
    if (!id) throw new Error('Attribute ID is required');
    if (!attributeData) throw new Error('Attribute data is required');
    try {
      const token = await auth.currentUser?.getIdToken(true);
      const response = await axios.put(`${API_BASE_URL}/attributes/${id}`, attributeData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error updating attribute:', error);
      throw error;
    }
  },

  // Delete an attribute
  deleteAttribute: async (id) => {
    if (!id) throw new Error('Attribute ID is required');
    try {
      const token = await auth.currentUser?.getIdToken(true);
      const response = await axios.delete(`${API_BASE_URL}/attributes/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error('Error deleting attribute:', error);
      throw error;
    }
  }
};

export const agentAttributesApi = {
  // Get all attribute values for a specific agent
  fetchAgentAttributes: async (memberId) => {
    if (!memberId) throw new Error('Member ID is required');
    try {
      const token = await auth.currentUser?.getIdToken(true);
      const response = await axios.get(`${API_BASE_URL}/agent-attributes/${memberId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching agent attributes:', error);
      throw error;
    }
  },

  // Update attribute values for a specific agent (bulk update)
  updateAgentAttributes: async (memberId, attributes) => {
    if (!memberId) throw new Error('Member ID is required');
    if (!attributes) throw new Error('Attributes data is required');
    try {
      const token = await auth.currentUser?.getIdToken(true);
      const response = await axios.put(`${API_BASE_URL}/agent-attributes/${memberId}`, 
        { attributes }, 
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating agent attributes:', error);
      throw error;
    }
  },

  // Remove a specific attribute assignment from an agent
  deleteAgentAttribute: async (memberId, attributeId) => {
    if (!memberId) throw new Error('Member ID is required');
    if (!attributeId) throw new Error('Attribute ID is required');
    try {
      const token = await auth.currentUser?.getIdToken(true);
      const response = await axios.delete(`${API_BASE_URL}/agent-attributes/${memberId}/${attributeId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error('Error deleting agent attribute:', error);
      throw error;
    }
  },

  // List all agent attribute assignments for a portal
  fetchPortalAgentAttributes: async (portalId) => {
    if (!portalId) throw new Error('Portal ID is required');
    try {
      const token = await auth.currentUser?.getIdToken(true);
      const response = await axios.get(`${API_BASE_URL}/attributes/portal/${portalId}`, {
        params: { limit: 500 },
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching portal agent attributes:', error);
      throw error;
    }
  }
};

export const memberCapsApi = {
  fetchMemberCaps: async (portalId, { limit = 100, offset = 0, includeInactive = false } = {}) => {
    if (!portalId) throw new Error('Portal ID is required');
    try {
      const token = await auth.currentUser?.getIdToken(true);
      const response = await axios.get(`${API_BASE_URL}/member-caps`, {
        params: {
          portal_id: portalId,
          limit,
          offset,
          include_inactive: includeInactive ? 'true' : 'false',
        },
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching member caps:', error);
      throw error;
    }
  },

  fetchAllMemberCaps: async (portalId, { includeInactive = false } = {}) => {
    if (!portalId) throw new Error('Portal ID is required');

    const pageSize = 100;
    let offset = 0;
    let hasMore = true;
    const caps = [];

    try {
      const token = await auth.currentUser?.getIdToken(true);

      while (hasMore) {
        const response = await axios.get(`${API_BASE_URL}/member-caps`, {
          params: {
            portal_id: portalId,
            limit: pageSize,
            offset,
            include_inactive: includeInactive ? 'true' : 'false',
          },
          headers: { Authorization: `Bearer ${token}` },
        });

        const pageCaps = response.data?.caps || [];
        caps.push(...pageCaps);

        const pagination = response.data?.pagination;
        hasMore = Boolean(pagination?.hasMore) && pageCaps.length > 0;
        offset = pagination
          ? Number(pagination.offset) + Number(pagination.limit)
          : offset + pageCaps.length;
      }

      return { caps };
    } catch (error) {
      console.error('Error fetching all member caps:', error);
      throw error;
    }
  },

  createMemberCap: async (capData) => {
    if (!capData) throw new Error('Cap data is required');
    try {
      const token = await auth.currentUser?.getIdToken(true);
      const response = await axios.post(`${API_BASE_URL}/member-caps`, capData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error creating member cap:', error);
      throw error;
    }
  },

  updateMemberCap: async (capId, capData) => {
    if (!capId) throw new Error('Cap ID is required');
    if (!capData) throw new Error('Cap data is required');
    try {
      const token = await auth.currentUser?.getIdToken(true);
      const response = await axios.put(`${API_BASE_URL}/member-caps/${capId}`, capData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error updating member cap:', error);
      throw error;
    }
  },

  deleteMemberCap: async (capId, portalId) => {
    if (!capId) throw new Error('Cap ID is required');
    try {
      const token = await auth.currentUser?.getIdToken(true);
      const response = await axios.delete(`${API_BASE_URL}/member-caps/${capId}`, {
        params: portalId ? { portal_id: portalId } : {},
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error('Error deleting member cap:', error);
      throw error;
    }
  },

  fetchMemberCapUsage: async (capId, portalId, { periodStart, limit = 50 } = {}) => {
    if (!capId) throw new Error('Cap ID is required');
    if (!portalId) throw new Error('Portal ID is required');
    try {
      const token = await auth.currentUser?.getIdToken(true);
      const response = await axios.get(`${API_BASE_URL}/member-caps/${capId}/usage`, {
        params: {
          portal_id: portalId,
          period_start: periodStart,
          limit,
        },
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching member cap usage:', error);
      throw error;
    }
  },
  exportMemberCaps: async (portalId, { includeInactive = false } = {}) => {
    if (!portalId) throw new Error('Portal ID is required');
    try {
      const token = await auth.currentUser?.getIdToken(true);
      const response = await axios.get(`${API_BASE_URL}/member-caps/export`, {
        params: {
          portal_id: portalId,
          include_inactive: includeInactive ? 'true' : 'false',
        },
        responseType: 'text',
        headers: { Authorization: `Bearer ${token}` },
      });
      return response;
    } catch (error) {
      console.error('Error exporting member caps:', error);
      throw error;
    }
  },
  importMemberCaps: async (portalId, csvText) => {
    if (!portalId) throw new Error('Portal ID is required');
    if (!csvText) throw new Error('CSV content is required');
    try {
      const token = await auth.currentUser?.getIdToken(true);
      const response = await axios.post(`${API_BASE_URL}/member-caps/import`, csvText, {
        params: { portal_id: portalId },
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'text/csv',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error importing member caps:', error);
      throw error;
    }
  },
};

export const externalCapsApi = {
  fetchExternalCaps: async (portalId, { includeInactive = false } = {}) => {
    if (!portalId) throw new Error('Portal ID is required');
    try {
      const token = await auth.currentUser?.getIdToken(true);
      const response = await axios.get(`${API_BASE_URL}/external-caps`, {
        params: {
          portal_id: portalId,
          include_inactive: includeInactive ? 'true' : 'false',
        },
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching external caps:', error);
      throw error;
    }
  },

  createExternalCap: async (payload) => {
    if (!payload) throw new Error('Payload is required');
    try {
      const token = await auth.currentUser?.getIdToken(true);
      const response = await axios.post(`${API_BASE_URL}/external-caps`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error creating external cap:', error);
      throw error;
    }
  },

  fetchExternalCap: async (configId, portalId) => {
    if (!configId) throw new Error('Config ID is required');
    try {
      const token = await auth.currentUser?.getIdToken(true);
      const response = await axios.get(`${API_BASE_URL}/external-caps/${configId}`, {
        params: portalId ? { portal_id: portalId } : {},
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching external cap:', error);
      throw error;
    }
  },

  updateExternalCap: async (configId, payload) => {
    if (!configId) throw new Error('Config ID is required');
    if (!payload) throw new Error('Payload is required');
    try {
      const token = await auth.currentUser?.getIdToken(true);
      const response = await axios.put(`${API_BASE_URL}/external-caps/${configId}`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error updating external cap:', error);
      throw error;
    }
  },

  deleteExternalCap: async (configId, portalId) => {
    if (!configId) throw new Error('Config ID is required');
    try {
      const token = await auth.currentUser?.getIdToken(true);
      const response = await axios.delete(`${API_BASE_URL}/external-caps/${configId}`, {
        params: portalId ? { portal_id: portalId } : {},
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error('Error deleting external cap:', error);
      throw error;
    }
  },

  syncExternalCapNow: async (configId, portalId) => {
    if (!configId) throw new Error('Config ID is required');
    try {
      const token = await auth.currentUser?.getIdToken(true);
      const response = await axios.post(
        `${API_BASE_URL}/external-caps/${configId}/sync`,
        {},
        {
          params: portalId ? { portal_id: portalId } : {},
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error syncing external cap:', error);
      throw error;
    }
  },

  fetchExternalCapUsage: async (configId, portalId, { periodStart, limit = 100 } = {}) => {
    if (!configId) throw new Error('Config ID is required');
    if (!portalId) throw new Error('Portal ID is required');
    try {
      const token = await auth.currentUser?.getIdToken(true);
      const response = await axios.get(`${API_BASE_URL}/external-caps/${configId}/usage`, {
        params: {
          portal_id: portalId,
          period_start: periodStart,
          limit,
        },
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching external cap usage:', error);
      throw error;
    }
  },
};

export const capPoliciesApi = {
  fetchPolicies: async (portalId) => {
    if (!portalId) throw new Error('Portal ID is required');
    const token = await auth.currentUser?.getIdToken(true);
    const response = await axios.get(`${API_BASE_URL}/cap-policies`, {
      params: { portal_id: portalId },
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  fetchPolicy: async (policyId, portalId) => {
    if (!policyId || !portalId) throw new Error('Policy ID and portal ID are required');
    const token = await auth.currentUser?.getIdToken(true);
    const response = await axios.get(`${API_BASE_URL}/cap-policies/${policyId}`, {
      params: { portal_id: portalId },
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  createPolicy: async (payload) => {
    const token = await auth.currentUser?.getIdToken(true);
    const response = await axios.post(`${API_BASE_URL}/cap-policies`, payload, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    });
    return response.data;
  },

  updatePolicy: async (policyId, payload) => {
    if (!policyId) throw new Error('Policy ID is required');
    const token = await auth.currentUser?.getIdToken(true);
    const response = await axios.put(`${API_BASE_URL}/cap-policies/${policyId}`, payload, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    });
    return response.data;
  },

  deactivatePolicy: async (policyId, portalId) => {
    if (!policyId || !portalId) throw new Error('Policy ID and portal ID are required');
    const token = await auth.currentUser?.getIdToken(true);
    const response = await axios.delete(`${API_BASE_URL}/cap-policies/${policyId}`, {
      params: { portal_id: portalId },
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },
};

export const portalSettingsApi = {
  fetchPortalSettings: async (portalId) => {
    if (!portalId) throw new Error('Portal ID is required');
    try {
      const token = await auth.currentUser?.getIdToken(true);
      const response = await axios.get(`${API_BASE_URL}/portal-settings`, {
        params: { portal_id: portalId },
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching portal settings:', error);
      throw error;
    }
  },
  fetchHealthAlertOptions: async (portalId) => {
    if (!portalId) throw new Error('Portal ID is required');
    const token = await auth.currentUser?.getIdToken(true);
    const response = await axios.get(`${API_BASE_URL}/portal-settings/health-alert-options`, {
      params: { portal_id: portalId },
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },
  updatePortalSettings: async (portalId, settings) => {
    if (!portalId) throw new Error('Portal ID is required');
    try {
      const token = await auth.currentUser?.getIdToken(true);
      const response = await axios.put(
        `${API_BASE_URL}/portal-settings`,
        {
          portal_id: portalId,
          fallback_owner_id: settings.fallbackOwnerId || null,
          cap_timezone: settings.capTimezone,
          health_alert_company_id: settings.healthAlertCompanyId || null,
          health_alert_property_name: settings.healthAlertPropertyName || null,
          health_alert_enabled: Boolean(settings.healthAlertEnabled),
          owner_fetch_mode: settings.ownerFetchMode ?? null,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating portal settings:', error);
      throw error;
    }
  },
  triggerOwnerCacheRefresh: async (portalId) => {
    if (!portalId) throw new Error('Portal ID is required');
    const token = await auth.currentUser?.getIdToken(true);
    await axios.post(`${API_BASE_URL}/owner-cache/refresh/${portalId}`, null, {
      headers: { Authorization: `Bearer ${token}` },
    });
  },
};

export const customObjectsApi = {
  list: async (portalId) => {
    if (!portalId) throw new Error('Portal ID is required');
    try {
      const token = await auth.currentUser?.getIdToken(true);
      const response = await axios.get(`${API_BASE_URL}/custom-objects`, {
        params: { portal_id: portalId },
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching custom objects:', error);
      throw error;
    }
  },
  create: async (payload) => {
    const token = await auth.currentUser?.getIdToken(true);
    const response = await axios.post(`${API_BASE_URL}/custom-objects`, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  },
  update: async (id, payload) => {
    if (!id) throw new Error('Custom object ID is required');
    const token = await auth.currentUser?.getIdToken(true);
    const response = await axios.put(`${API_BASE_URL}/custom-objects/${id}`, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  },
  remove: async (id, portalId) => {
    if (!id) throw new Error('Custom object ID is required');
    if (!portalId) throw new Error('Portal ID is required');
    const token = await auth.currentUser?.getIdToken(true);
    const response = await axios.delete(`${API_BASE_URL}/custom-objects/${id}`, {
      params: { portal_id: portalId },
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },
};

export const salesContentApi = {
  // Fetch sales content for a portal
  fetchSalesContent: async (portalId) => {
    if (!portalId) throw new Error('Portal ID is required');
    try {
      const token = await auth.currentUser?.getIdToken(true);
      const response = await axios.get(`${API_BASE_URL}/agents/content`, {
        params: { portal_id: portalId },
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching sales content:', error);
      throw error;
    }
  },

  // Create new sales content
  createSalesContent: async (contentData, portalId) => {
    if (!portalId) throw new Error('Portal ID is required');
    try {
      const token = await auth.currentUser?.getIdToken(true);
      
      // Prepare the request payload
      const payload = {
        ...contentData,
        portalId: portalId,
        timestamp: new Date().toISOString(),
      };

      // Use the new agents API endpoint
      const AGENTS_API_URL = process.env.REACT_APP_AGENTS_API_URL || `${API_BASE_URL.replace('/api', '/agents-api')}`;
      const response = await axios.post(`${AGENTS_API_URL}/distributely-sales-content`, payload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      return response.data;
    } catch (error) {
      console.error('Error creating sales content:', error);
      throw error;
    }
  },

  deleteSalesContent: async (id) => {
    if (!id) throw new Error('Content ID is required');
    try {
      const token = await auth.currentUser?.getIdToken(true);
      const response = await axios.delete(`${API_BASE_URL}/agents/content/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error(`Error deleting sales content with id ${id}:`, error);
      throw error;
    }
  }
};

export const distributionExplanationApi = {
  /**
   * Fetch distribution record for a specific object
   * @param {Object} params - Request parameters
   * @param {number} params.portalId - Portal ID
   * @param {string} params.objectId - Object ID
   * @param {string} params.objectType - Object Type (CONTACT, COMPANY, DEAL, TICKET)
   * @returns {Promise<Object>} API response with distribution record
   */
  fetchDistributionRecord: async ({ portalId, objectId, objectType }) => {
    if (!portalId) throw new Error('Portal ID is required');
    if (!objectId) throw new Error('Object ID is required');
    if (!objectType) throw new Error('Object Type is required');

    try {
      const payload = {
        portalId: parseInt(portalId, 10),
        objectId: objectId,
        objectType: objectType,
      };

      const response = await axios.post(`${AGENTS_API_URL}/distributely-fetch-distribution-record`, payload, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      return response.data;
    } catch (error) {
      console.error('Error fetching distribution record:', error);
      throw error;
    }
  },

  /**
   * Get explanation for a distribution record
   * @param {Object} record - Distribution record object
   * @returns {Promise<Object>} API response with explanation
   */
  explainDistribution: async (record, analysisMode = 'analytics-record') => {
    if (!record) throw new Error('Distribution record is required');

    let payload = {
      record: record,
      analysisMode,
    }

    try {
      const response = await axios.post(`${AGENTS_API_URL}/distributely-explain-distribution`, payload, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      return response.data;
    } catch (error) {
      console.error('Error getting distribution explanation:', error);
      throw error;
    }
  },
  /**
   * Check job status by jobId
   * @param {string} jobId - Job ID returned from explainDistribution
   * @returns {Promise<Object>} Job status object
   */
  getJobStatus: async (jobId) => {
    if (!jobId) throw new Error('Job ID is required');

    try {
      const response = await axios.post(`${AGENTS_API_URL}/distributely-fetch-distribution-job`, {
        jobId: jobId
      }, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      return response.data;
    } catch (error) {
      console.error('Error fetching job status:', error);
      throw error;
    }
  }


};

export const configurationAgentApi = {
  startChat: async ({ portalId, sessionId, message, context }) => {
    if (!portalId) throw new Error('Portal ID is required');
    if (!message) throw new Error('Message is required');

    const payload = {
      portalId: parseInt(portalId, 10),
      sessionId: sessionId || undefined,
      message,
      context: context || {},
    };

    try {
      const response = await axios.post(`${AGENTS_API_URL}/distributely-configuration-chat`, payload, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      return response.data;
    } catch (error) {
      console.error('Error starting configuration chat:', error);
      throw error;
    }
  },

  getChatJobStatus: async (jobId) => {
    if (!jobId) throw new Error('Job ID is required');

    try {
      const response = await axios.post(`${AGENTS_API_URL}/distributely-configuration-chat-job`, {
        jobId,
      }, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      return response.data;
    } catch (error) {
      console.error('Error fetching configuration chat job status:', error);
      throw error;
    }
  },
};

export const analyticsApi = {
  /**
   * Query analytics data
   * @param {Object} params - Request parameters
   * @param {number} params.portalId - Portal ID
   * @param {string} params.objectType - Object Type (contact, company, deal, lead, ticket)
   * @param {string} params.startISO - Start date in ISO format (UTC)
   * @param {string} params.endISO - End date in ISO format (UTC)
   * @param {string} params.reportType - Report type (assignments_by_owner, executive_summary, all)
   * @returns {Promise<Object>} API response with analytics data
   */
  queryAnalytics: async ({ portalId, objectType, startISO, endISO, reportType }) => {
    if (!portalId) throw new Error('Portal ID is required');
    if (!startISO) throw new Error('Start date is required');
    if (!endISO) throw new Error('End date is required');

    try {
      const token = await auth.currentUser?.getIdToken(true);
      const payload = {
        portalId: parseInt(portalId, 10),
        objectType: objectType || null,
        startISO,
        endISO,
        reportType: reportType || 'assignments_by_owner',
      };

      console.log('Analytics payload:', payload);

      const response = await axios.post(`${API_BASE_URL}/analytics/query`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      console.log('Analytics response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error querying analytics:', error);
      throw error;
    }
  },

  queryRecentDistributions: async ({
    portalId,
    objectType,
    startISO,
    endISO,
    page = 1,
    limit = 50,
  }) => {
    if (!portalId) throw new Error('Portal ID is required');
    if (!startISO) throw new Error('Start date is required');
    if (!endISO) throw new Error('End date is required');

    try {
      const token = await auth.currentUser?.getIdToken(true);
      const payload = {
        portalId: parseInt(portalId, 10),
        objectType: objectType || null,
        startISO,
        endISO,
        reportType: 'recent_distributions',
        page,
        limit,
      };
      const response = await axios.post(`${API_BASE_URL}/analytics/query`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        validateStatus: (status) => status >= 200 && status < 300,
      });
      return {
        statusCode: response.status,
        data: response.data,
      };
    } catch (error) {
      console.error('Error querying recent distributions:', error);
      throw error;
    }
  },
};

export const ecommerceApi = {
  fetchAccountStatus: async () => {
    try {
      const token = await auth.currentUser?.getIdToken(true);
      if (!token) throw new Error('Authentication required');

      const response = await axios.get(`${API_BASE_URL}/ecommerce/account`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching ecommerce account status:', error);
      throw error;
    }
  },

  createSetupIntent: async () => {
    try {
      const token = await auth.currentUser?.getIdToken(true);
      if (!token) throw new Error('Authentication required');

      const response = await axios.post(
        `${API_BASE_URL}/ecommerce/payment-method/setup-intent`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error('Error creating setup intent:', error);
      throw error;
    }
  },

  confirmPaymentMethodSetup: async (setupIntentId) => {
    try {
      const token = await auth.currentUser?.getIdToken(true);
      if (!token) throw new Error('Authentication required');
      if (!setupIntentId) throw new Error('setupIntentId is required');

      const response = await axios.post(
        `${API_BASE_URL}/ecommerce/payment-method/confirm`,
        { setupIntentId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error('Error confirming payment method setup:', error);
      throw error;
    }
  },
};

export const talkdeskApi = {
  getStatus: async (portalId) => {
    const token = await auth.currentUser?.getIdToken(true);
    const response = await axios.get(`${API_BASE_URL}/talkdesk/status`, {
      params: { portal_id: portalId },
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  connect: async ({ portal_id, account_name, client_id, client_secret }) => {
    const token = await auth.currentUser?.getIdToken(true);
    const response = await axios.post(
      `${API_BASE_URL}/talkdesk/connect`,
      { portal_id, account_name, client_id, client_secret },
      { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
    );
    return response.data;
  },
};
